import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';
import type { Row, ColumnPinningState } from '@tanstack/react-table';
import { useEffect, useRef } from 'react';
import type { EventState } from 'features/misc/table';

export type VirtualRowItem<T> = Row<T> | { isExpandedRow: boolean; parentRow: Row<T> };

export const useVirtual = <T,>(
  rows: VirtualRowItem<T>[],
  container: React.MutableRefObject<HTMLDivElement | null>,
  disabled = false,
  rowHeight: number,
  expandedRowHeight?: number,
  overscan = 20
) => {
  const { getVirtualItems: items, getTotalSize: size } = useVirtualizer({
    getScrollElement: () => container.current,
    count: rows.length,
    estimateSize: index => {
      const row = rows[index];
      if (row && typeof row === 'object' && 'isExpandedRow' in row) {
        return expandedRowHeight ?? rowHeight;
      }
      return rowHeight;
    },
    overscan,
  });

  const getVirtualItems = (): VirtualItem[] => {
    if (disabled) {
      let currentStart = 0;
      return rows.map((row, index) => {
        const isExpandedRow = typeof row === 'object' && 'isExpandedRow' in row;
        const itemHeight = isExpandedRow ? expandedRowHeight ?? rowHeight : rowHeight;
        const virtualItem = {
          key: isExpandedRow ? `${row.parentRow.id}-expanded` : row.id,
          index,
          start: currentStart,
          end: currentStart + itemHeight,
          size: itemHeight,
          lane: 0,
        };
        currentStart += itemHeight;
        return virtualItem;
      });
    }
    return items();
  };

  const getTotalSize = () => {
    if (disabled) {
      return rows.reduce((total, row) => {
        const isExpandedRow = typeof row === 'object' && 'isExpandedRow' in row;
        return total + (isExpandedRow ? expandedRowHeight ?? rowHeight : rowHeight);
      }, 0);
    }
    return size();
  };

  return { getVirtualItems, getTotalSize };
};

export const useTableEvents = (
  columnVisibility: Record<string, boolean>,
  columnOrder: string[],
  columnPinning: ColumnPinningState,
  columnSizing: Record<string, number>,
  rowSelection: Record<string, boolean>,
  onEvent?: (name: string, state: EventState) => void
) => {
  const state = useRef({
    columnVisibility: '',
    columnOrder: '',
    columnPinning: '',
    columnSizing: '',
    rowSelection: '',
  });
  const handleEvent = (
    name: 'columnVisibility' | 'columnOrder' | 'columnPinning' | 'columnSizing' | 'rowSelection',
    eventState: EventState
  ) => {
    if (!onEvent) return;
    if (!state.current[name]) {
      state.current[name] = JSON.stringify(eventState);
      return;
    }
    if (state.current[name] === JSON.stringify(eventState)) return;
    onEvent(name, eventState);
    state.current[name] = JSON.stringify(eventState);
  };
  useEffect(() => {
    handleEvent('columnVisibility', columnVisibility);
  }, [columnVisibility]);
  useEffect(() => {
    handleEvent('columnOrder', columnOrder);
  }, [columnOrder]);
  useEffect(() => {
    handleEvent('columnPinning', columnPinning.left || []);
  }, [columnPinning]);
  useEffect(() => {
    handleEvent('columnSizing', columnSizing);
  }, [columnSizing]);
  useEffect(() => {
    handleEvent('rowSelection', rowSelection);
  }, [rowSelection]);
};
